import { openDB } from "idb";
import { normalizeMessages } from "@/utils/common";

export const MESSAGE_PAGE_SIZE = 50;

const DB_NAME = "chat-db";
const DB_VERSION = 2;
const MESSAGE_STORE = "room_messages";
const PAGE_STORE = "message_pages";
const LEGACY_MESSAGE_STORE = "messages";
const LATEST_PAGE_CURSOR = "latest";

const MESSAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_TOTAL_MESSAGES = 5000;
const MAX_ROOM_MESSAGES = 800;

const saveTimers = {};

const hasValue = (value) => value !== undefined && value !== null && value !== "";

const normalizeRoomId = (roomId) => {
  const numericRoomId = Number(roomId);
  return Number.isNaN(numericRoomId) ? roomId : numericRoomId;
};

const normalizeCursor = (cursorId) => {
  if (!hasValue(cursorId)) return LATEST_PAGE_CURSOR;

  const numericCursor = Number(cursorId);
  return Number.isNaN(numericCursor) ? cursorId : numericCursor;
};

const getMessageKey = (message) => {
  if (!message) return null;

  const numericId = Number(message.id);
  if (hasValue(message.id) && !Number.isNaN(numericId) && numericId > 0) return numericId;
  if (hasValue(message.local_id)) return message.local_id;

  return null;
};

const getMessageSortId = (message) => {
  const key = getMessageKey(message);
  const numericKey = Number(key);

  if (!Number.isNaN(numericKey) && numericKey > 0) return numericKey;

  const createdAt = Number(message?.created_at || 0);
  return createdAt || 0;
};

const buildMessageCacheId = (roomId, messageKey) => `${roomId}:${messageKey}`;

const buildPageCacheId = (roomId, cursorId) => `${roomId}:${normalizeCursor(cursorId)}`;

const compareMessages = (a, b) => {
  const aCreatedAt = Number(a.created_at || 0);
  const bCreatedAt = Number(b.created_at || 0);

  if (aCreatedAt !== bCreatedAt) return aCreatedAt - bCreatedAt;

  return getMessageSortId(a) - getMessageSortId(b);
};

const isSameMessage = (current, next) => {
  if (hasValue(next.local_id) && hasValue(current.local_id) && current.local_id === next.local_id) return true;

  const nextKey = getMessageKey(next);
  const currentKey = getMessageKey(current);

  return hasValue(nextKey) && hasValue(currentKey) && nextKey === currentKey;
};

const mergeMessages = (...messageGroups) => {
  const mergedMessages = [];
  const messages = normalizeMessages(messageGroups.flat()).filter(Boolean);

  for (const message of messages) {
    const messageIndex = mergedMessages.findIndex((item) => isSameMessage(item, message));

    if (messageIndex === -1) {
      mergedMessages.push(message);
    } else {
      mergedMessages[messageIndex] = {
        ...mergedMessages[messageIndex],
        ...message,
      };
    }
  }

  return mergedMessages.sort(compareMessages);
};

const isExpired = (record, now = Date.now()) => {
  const timestamp = record?.updated_at || record?.cached_at || 0;
  return !timestamp || now - timestamp > MESSAGE_TTL_MS;
};

const createStores = (db) => {
  if (!db.objectStoreNames.contains(MESSAGE_STORE)) {
    const store = db.createObjectStore(MESSAGE_STORE, {
      keyPath: "cache_id",
    });
    store.createIndex("room_id", "room_id");
    store.createIndex("updated_at", "updated_at");
  }

  if (!db.objectStoreNames.contains(PAGE_STORE)) {
    const store = db.createObjectStore(PAGE_STORE, {
      keyPath: "cache_id",
    });
    store.createIndex("room_id", "room_id");
    store.createIndex("updated_at", "updated_at");
  }
};

export const getDB = async (userId) => {
  if (!userId) {
    throw new Error("userId is required");
  }

  const dbName = `${DB_NAME}-${userId}`;

  return openDB(dbName, DB_VERSION, {
    upgrade(db) {
      createStores(db);
    },
  });
};

const deleteRecords = async (db, storeName, keys) => {
  if (!keys.length) return;

  const tx = db.transaction(storeName, "readwrite");

  keys.forEach((key) => {
    tx.store.delete(key);
  });

  await tx.done;
};

const getRoomMessageRecords = async (db, roomId) => {
  const records = await db.getAllFromIndex(MESSAGE_STORE, "room_id", roomId);
  const now = Date.now();
  const expiredKeys = records
    .filter((record) => isExpired(record, now))
    .map((record) => record.cache_id);

  await deleteRecords(db, MESSAGE_STORE, expiredKeys);

  return records.filter((record) => !expiredKeys.includes(record.cache_id));
};

const getLegacyRoomMessages = async (db, roomId, limit) => {
  if (!db.objectStoreNames.contains(LEGACY_MESSAGE_STORE)) return null;

  const cached = await db.get(LEGACY_MESSAGE_STORE, roomId);
  if (!cached) return null;

  if (isExpired(cached)) {
    await db.delete(LEGACY_MESSAGE_STORE, roomId);
    return null;
  }

  const messages = mergeMessages(cached.messages).slice(-limit);

  return {
    ...cached,
    initialized: Boolean(cached.initialized || messages.length),
    messages,
  };
};

const readPageMessages = async (db, page) => {
  if (!page) return null;

  if (isExpired(page)) {
    await db.delete(PAGE_STORE, page.cache_id);
    return null;
  }

  if (!page.message_ids?.length) {
    return {
      ...page,
      messages: [],
    };
  }

  const records = await Promise.all(
    page.message_ids.map((messageId) => db.get(MESSAGE_STORE, buildMessageCacheId(page.room_id, messageId)))
  );

  if (records.some((record) => !record || isExpired(record))) return null;

  return {
    ...page,
    messages: mergeMessages(records.map((record) => record.message)),
  };
};

export const cleanupLocalMessages = async (userId) => {
  try {
    const db = await getDB(userId);
    const now = Date.now();
    const messages = await db.getAll(MESSAGE_STORE);
    const pages = await db.getAll(PAGE_STORE);
    const messageDeleteKeys = new Set();
    const pageDeleteKeys = new Set();

    messages.forEach((record) => {
      if (isExpired(record, now)) messageDeleteKeys.add(record.cache_id);
    });

    pages.forEach((record) => {
      if (isExpired(record, now)) pageDeleteKeys.add(record.cache_id);
    });

    const activeMessages = messages.filter((record) => !messageDeleteKeys.has(record.cache_id));
    const rooms = new Map();

    activeMessages.forEach((record) => {
      const roomMessages = rooms.get(record.room_id) || [];
      roomMessages.push(record);
      rooms.set(record.room_id, roomMessages);
    });

    rooms.forEach((roomMessages) => {
      roomMessages
        .sort((a, b) => getMessageSortId(b.message) - getMessageSortId(a.message))
        .slice(MAX_ROOM_MESSAGES)
        .forEach((record) => messageDeleteKeys.add(record.cache_id));
    });

    activeMessages
      .filter((record) => !messageDeleteKeys.has(record.cache_id))
      .sort((a, b) => (b.accessed_at || b.updated_at || 0) - (a.accessed_at || a.updated_at || 0))
      .slice(MAX_TOTAL_MESSAGES)
      .forEach((record) => messageDeleteKeys.add(record.cache_id));

    await Promise.all([
      deleteRecords(db, MESSAGE_STORE, Array.from(messageDeleteKeys)),
      deleteRecords(db, PAGE_STORE, Array.from(pageDeleteKeys)),
    ]);
  } catch (err) {
    console.error("Failed to cleanup local messages:", err);
  }
};

export const saveRoomMessagePage = async (
  userId,
  room_id,
  messages,
  options = {}
) => {
  const roomId = normalizeRoomId(room_id);
  const limit = options.limit || MESSAGE_PAGE_SIZE;
  const normalizedMessages = mergeMessages(messages).filter((message) => hasValue(getMessageKey(message)));
  const cursorId = normalizeCursor(options.cursorId);
  const now = Date.now();

  if (!hasValue(roomId)) return;
  if (!normalizedMessages.length && cursorId === LATEST_PAGE_CURSOR) return;

  try {
    const db = await getDB(userId);
    const tx = db.transaction([MESSAGE_STORE, PAGE_STORE], "readwrite");
    const messageStore = tx.objectStore(MESSAGE_STORE);
    const pageStore = tx.objectStore(PAGE_STORE);
    const messageIds = [];

    for (const message of normalizedMessages) {
      const messageKey = getMessageKey(message);
      const cacheId = buildMessageCacheId(roomId, messageKey);
      const existing = await messageStore.get(cacheId);

      messageIds.push(messageKey);

      await messageStore.put({
        cache_id: cacheId,
        room_id: roomId,
        message_id: messageKey,
        sort_id: getMessageSortId(message),
        cached_at: existing?.cached_at || now,
        updated_at: now,
        accessed_at: now,
        message: {
          ...(existing?.message || {}),
          ...message,
          room_id: roomId,
        },
      });
    }

    await pageStore.put({
      cache_id: buildPageCacheId(roomId, cursorId),
      room_id: roomId,
      cursor_id: cursorId,
      message_ids: messageIds,
      first_message_id: normalizedMessages[0]?.id || null,
      last_message_id: normalizedMessages[normalizedMessages.length - 1]?.id || null,
      limit,
      is_complete: options.isComplete ?? normalizedMessages.length < limit,
      cached_at: now,
      updated_at: now,
      accessed_at: now,
    });

    await tx.done;
    await cleanupLocalMessages(userId);
  } catch (err) {
    console.error("Failed to save room message page:", err);
  }
};

export const initializeRoomMessages = async (userId, room_id, messages, options = {}) => {
  await saveRoomMessagePage(userId, room_id, messages, {
    cursorId: LATEST_PAGE_CURSOR,
    limit: options.limit || MESSAGE_PAGE_SIZE,
    isComplete: options.isComplete,
  });
};

export const saveLocalRoomMessages = async (userId, room_id, messages) => {
  const roomId = normalizeRoomId(room_id);
  const normalizedMessages = mergeMessages(messages).filter((message) => hasValue(getMessageKey(message)));

  if (!userId || !hasValue(roomId) || !normalizedMessages.length) return;

  try {
    const db = await getDB(userId);
    const tx = db.transaction(MESSAGE_STORE, "readwrite");

    for (const message of normalizedMessages) {
      const messageKey = getMessageKey(message);
      const cacheId = buildMessageCacheId(roomId, messageKey);
      const existing = await tx.store.get(cacheId);
      const now = Date.now();

      await tx.store.put({
        cache_id: cacheId,
        room_id: roomId,
        message_id: messageKey,
        sort_id: getMessageSortId(message),
        cached_at: existing?.cached_at || now,
        updated_at: now,
        accessed_at: now,
        message: {
          ...(existing?.message || {}),
          ...message,
          room_id: roomId,
        },
      });
    }

    await tx.done;
    await cleanupLocalMessages(userId);
  } catch (err) {
    console.error("Failed to save local room messages:", err);
  }
};

export const getLocalRoomMessagePage = async (
  userId,
  room_id,
  cursorId,
  limit = MESSAGE_PAGE_SIZE
) => {
  if (!userId || !hasValue(room_id) || !hasValue(cursorId)) return null;

  const roomId = normalizeRoomId(room_id);
  const db = await getDB(userId);
  const page = await db.get(PAGE_STORE, buildPageCacheId(roomId, cursorId));
  const cachedPage = await readPageMessages(db, page);

  if (!cachedPage) return null;

  const hasEnough = cachedPage.messages.length >= limit;
  if (!hasEnough && !cachedPage.is_complete) return null;

  return {
    initialized: true,
    fromCache: true,
    isComplete: cachedPage.is_complete,
    messages: cachedPage.messages.slice(-limit),
  };
};

export const getLocalRoomMessages = async (userId, room_id, options = {}) => {
  if (!userId || !hasValue(room_id)) return null;

  const roomId = normalizeRoomId(room_id);
  const limit = options.limit || MESSAGE_PAGE_SIZE;

  if (hasValue(options.cursorId)) {
    return getLocalRoomMessagePage(userId, roomId, options.cursorId, limit);
  }

  const db = await getDB(userId);
  const records = await getRoomMessageRecords(db, roomId);
  const messages = mergeMessages(records.map((record) => record.message)).slice(-limit);

  if (messages.length) {
    return {
      initialized: true,
      fromCache: true,
      messages,
    };
  }

  return getLegacyRoomMessages(db, roomId, limit);
};

export const deleteLocalMessage = async (
  userId,
  room_id,
  message_ids
) => {
  if (!userId || !hasValue(room_id) || !message_ids?.length) return;

  try {
    const roomId = normalizeRoomId(room_id);
    const db = await getDB(userId);
    const tx = db.transaction([MESSAGE_STORE, PAGE_STORE], "readwrite");
    const messageStore = tx.objectStore(MESSAGE_STORE);
    const pageStore = tx.objectStore(PAGE_STORE);
    const deleteIds = new Set(message_ids.map((messageId) => normalizeCursor(messageId)));

    deleteIds.forEach((messageId) => {
      messageStore.delete(buildMessageCacheId(roomId, messageId));
    });

    const pages = await pageStore.index("room_id").getAll(roomId);

    for (const page of pages) {
      const nextMessageIds = (page.message_ids || []).filter((messageId) => !deleteIds.has(normalizeCursor(messageId)));

      if (nextMessageIds.length !== page.message_ids?.length) {
        await pageStore.put({
          ...page,
          message_ids: nextMessageIds,
          updated_at: Date.now(),
        });
      }
    }

    await tx.done;

    if (db.objectStoreNames.contains(LEGACY_MESSAGE_STORE)) {
      const legacyCached = await db.get(LEGACY_MESSAGE_STORE, roomId);

      if (legacyCached?.messages?.length) {
        await db.put(LEGACY_MESSAGE_STORE, {
          ...legacyCached,
          messages: legacyCached.messages.filter((message) => !deleteIds.has(normalizeCursor(message.id))),
          updated_at: Date.now(),
        });
      }
    }
  } catch (err) {
    console.error(err);
  }
};

export const clearAllLocalMessages = async (userId) => {
  const db = await getDB(userId);
  const stores = [MESSAGE_STORE, PAGE_STORE, LEGACY_MESSAGE_STORE].filter((storeName) =>
    db.objectStoreNames.contains(storeName)
  );

  await Promise.all(stores.map((storeName) => db.clear(storeName)));
};

export const enqueueSaveRoomMessages = (
  userId,
  room_id,
  messages
) => {
  if (!userId || room_id === undefined || room_id === null || !messages?.length) return;

  const timerKey = `${userId}:${room_id}`;

  if (saveTimers[timerKey]) {
    clearTimeout(saveTimers[timerKey]);
  }

  saveTimers[timerKey] = setTimeout(async () => {
    delete saveTimers[timerKey];

    await saveLocalRoomMessages(userId, room_id, messages);
  }, 500);
};
